import { useState } from "react";

import { useDispatch } from "react-redux";

import { showError, showSuccess } from "@/utils/notification";

import { ReportGeneratorTemplateWithTypeSchema } from "@api/generated/types.gen";

import fileWithTrashIcon from "@assets/images/file-with-trash.png";
import { ReactComponent as EditIcon } from "@assets/svg/document-with-pen.svg";
import { ReactComponent as DeleteIcon } from "@assets/svg/trash-red.svg";

import { ConfirmationModal } from "@components/ConfirmationModal";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";

import { useDeleteTemplate } from "@hooks/mutations/use-templates";

import { formatDateToLocal } from "@utils/dateUtils";
import { generateNumericalId, toTitleCase } from "@utils/string";
import { cn } from "@utils/ui";

import { TEMPLATE_LIST_GRID_COLS } from "../constants/columns";
import { EditTemplateModal } from "./EditTemplateModal";
import { TemplateTypeBadge } from "./TemplateTypeBadge";

type TemplateCardProps = {
  template: ReportGeneratorTemplateWithTypeSchema;
  allTemplates: ReportGeneratorTemplateWithTypeSchema[];
};

export const TemplateCard = ({ template, allTemplates }: TemplateCardProps) => {
  const dispatch = useDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const sections = template.sections ?? [];
  const totalSections = sections.length;
  const midpoint = Math.ceil(totalSections / 2);
  const sectionColumns = [sections.slice(0, midpoint), sections.slice(midpoint)];

  const createdDate = formatDateToLocal(template.created_at, "MMM d, yyyy");
  const numericalId = generateNumericalId(template.id);

  const deleteTemplate = useDeleteTemplate({
    onSuccess: () => {
      showSuccess("Template deleted successfully!", dispatch);
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      console.error("Failed to delete template:", error);
      showError("Failed to delete template. Please try again.", dispatch);
    },
  });

  return (
    <>
      <AccordionItem
        value={template.id}
        className="border-b border-border last:border-b-0"
      >
        <AccordionTrigger className="px-4 py-3 text-sm font-medium text-foreground no-underline hover:bg-[#E8FDFF]/50 hover:no-underline data-[state=open]:bg-primary-50/60">
          <div
            className={cn(
              "grid flex-1 items-center gap-4 text-left",
              TEMPLATE_LIST_GRID_COLS
            )}
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="font-head text-[13px] font-semibold text-foreground">
                  {template.name}
                </span>
                <TemplateTypeBadge isCustom={template.is_custom} />
              </div>
              <div className="pt-1 text-xs text-foreground-muted">
                Template {numericalId}
                {template.state && ` · State: ${template.state}`}
              </div>
            </div>
            <div className="font-head text-[13px] text-foreground">
              {totalSections}
            </div>
            <div className="font-head text-[13px] text-foreground-muted">
              {createdDate}
            </div>
          </div>
        </AccordionTrigger>

        <AccordionContent className="px-4 pb-4 pt-2">
          <div className="rounded-xl border border-border bg-background-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
              <div className="flex items-center gap-2">
                <span className="font-head text-xs font-semibold uppercase tracking-wide text-foreground-muted">
                  Report Sections
                </span>
                <Badge variant="secondary">{totalSections}</Badge>
              </div>

              {template.is_custom && (
                <div className="flex gap-2 divide-x divide-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditModalOpen(true)}
                    className="gap-2 text-primary hover:text-primary"
                  >
                    <EditIcon className="size-4" />
                    Edit Sections
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="gap-2 text-destructive hover:text-destructive"
                  >
                    <DeleteIcon className="size-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              {sectionColumns.map((column, columnIndex) => (
                <div key={columnIndex} className="divide-y divide-border">
                  {column.map((section, index) => (
                    <div key={section.id} className="flex items-center gap-3 py-2.5">
                      <span className="w-5 shrink-0 font-head text-xs font-bold text-primary">
                        {columnIndex === 0 ? index + 1 : midpoint + index + 1}
                      </span>
                      <span className="font-head text-[13px] font-medium text-foreground-muted">
                        {toTitleCase(section.name)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        template={template}
        templates={allTemplates}
      />

      <ConfirmationModal
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        type="warning"
        icon={
          <img src={fileWithTrashIcon} alt="Delete template" className="size-12" />
        }
        title="Delete Selected Template?"
        description={`This action will permanently delete ${template.name} and cannot be undone. Please confirm to proceed.`}
        confirmButtonText={deleteTemplate.isPending ? "Deleting..." : "Yes, Delete"}
        cancelButtonText="No, Cancel"
        onConfirm={() => deleteTemplate.mutate(template.id)}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isLoading={deleteTemplate.isPending}
      />
    </>
  );
};
